import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useSelector, useDispatch } from "react-redux"
import { useParams } from "react-router"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import "../../../Styles/main.scss"
import { CloseIcon, CubeIcon, EditProfileSmallIcon, FileIcon, SendIcon } from "./SvgIcons"
import { acceptedFileTypes, fileImageType } from "../../../App/GlobaleVariables"
import { RootState } from "../../../App/store"
import { ConvertStringToHTML } from "../../../App/Helpers/Helpers"
import { EditorModifier, RoleType, StorageKey } from "../../../App/enums"
import Image from "../../Shared/Primitive/Image"
import { hashtagRegex } from "../../../App/Regex"
import EditorTextArea from "./Editor/EditorTextArea"
import { Editor as TinyMCEEditor } from "tinymce"
import SearchableRoomDropdown from "./SearchableRoomDropdown"
import { roomPostPostRequest } from "../../Room/RoomPost/RoomPostRequests"
import { MentionedUsersType } from "../../../Types/RoomPosts"
import TutorialBubble from "./TutorialBubble"
import DocumentTextAreaPreview from "./DocumentTextAreaPreview"
import CharacterCounter from "./CharacterCounter"
import useUnsavedChangesWarning from "../Hooks/useUnsavedChangesWarning"
import UnsavedChangesModal from "./UnsavedChangesModal"
import useWindowSize from "../Hooks/useWindowSize"
import SessionService from "../../../App/SessionService"

function FeedTextBox(props: { compactOnDesktop?: boolean; hideCompactTrigger?: boolean }) {
    const translation = useSelector(selectTranslations)
    const { id: organizationRouteId } = useParams()
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const draftStorageKey = `${StorageKey.feedComposerDraft}_${role}_${role === RoleType.ORGANIZATION ? organizationRouteId : userDataState.id}`
    const [draftContent] = useState<string>(() => SessionService.getItem(draftStorageKey) || "")
    const [isDraftNoticeVisible, setIsDraftNoticeVisible] = useState<boolean>(() => draftContent.trim().length > 0)
    const [inputState, setInputState] = useState<string>(draftContent)
    const editorRef = useRef<TinyMCEEditor | null>(null)
    const [content, setContent] = useState<string>(() => ConvertStringToHTML(draftContent)?.textContent || "")
    const [roomId, setRoomId] = useState(0)
    const posts = useSelector((state: RootState) => state.RoomPostsSlice.roomPostsSlice.posts)
    const dispatch = useDispatch()
    const photoSizeErrorState = useSelector((state: RootState) => state.roomSlice.photoSizeErrorSlice.message)
    const postContentErrorState = useSelector((state: RootState) => state.RoomPostsSlice.roomPostContentErrorSlice.message)
    const [validationError] = useState("")
    const [image, setImage] = useState<any>(null)
    const [dropdownKey, setDropdownKey] = useState(0)
    const [fileType, setFileType] = useState("")
    const [documentState, setDocumentState] = useState<any>(null)
    const [previewUrl, setPreviewUrl] = useState<string>()
    let fileRef = React.useRef<HTMLInputElement>(null)
    const contentMaxLength = roomId! === 239 ? 15000 : 2000
    const hasUnsavedPostChanges = content.trim().length > 0 || image !== null || documentState !== null
    const screenWidth = useWindowSize()
    const mobileBreakpoint = 1023
    const isMobile = screenWidth < mobileBreakpoint
    const isCompactMode = isMobile || props.compactOnDesktop === true
    const [isComposerExpanded, setIsComposerExpanded] = useState(false)
    const shouldHideCollapsedComposer = isCompactMode && !isComposerExpanded && props.hideCompactTrigger === true && isMobile
    const [isSubmitting, setIsSubmitting] = useState(false)

    const focusComposerEditor = () => {
        let attempts = 0
        const maxAttempts = 12

        const tryFocus = () => {
            if (editorRef.current) {
                editorRef.current.focus()
                return
            }

            attempts += 1
            if (attempts < maxAttempts) {
                window.setTimeout(tryFocus, 60)
            }
        }

        tryFocus()
    }

    const onCompactComposerClick = () => {
        setIsComposerExpanded(true)
    }

    const closeComposer = () => {
        editorRef.current?.setContent("")
        setInputState("")
        setContent("")
        setImage(null)
        setDocumentState(null)
        setPreviewUrl(undefined)
        setFileType("")
        if (fileRef.current) {
            fileRef.current.value = ""
        }
        setIsComposerExpanded(false)
        setIsDraftNoticeVisible(false)
        SessionService.removeItem(draftStorageKey)
    }

    const discardDraft = () => {
        editorRef.current?.setContent("")
        setInputState("")
        setContent("")
        setIsDraftNoticeVisible(false)
        SessionService.removeItem(draftStorageKey)
    }

    useEffect(() => {
        if (isCompactMode && isComposerExpanded) {
            window.setTimeout(() => focusComposerEditor(), 0)
        }
    }, [isCompactMode, isComposerExpanded])

    useEffect(() => {
        if (isCompactMode && draftContent.trim().length > 0) {
            setIsComposerExpanded(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (inputState.trim().length > 0) {
            SessionService.setItem(draftStorageKey, inputState)
        } else {
            SessionService.removeItem(draftStorageKey)
        }
    }, [inputState, draftStorageKey])

    useEffect(() => {
        const isComposerFocusActive = isCompactMode && isComposerExpanded

        if (isComposerFocusActive) {
            document.body.classList.add("feed-composer-focus-active")
        } else {
            document.body.classList.remove("feed-composer-focus-active")
        }

        return () => {
            document.body.classList.remove("feed-composer-focus-active")
        }
    }, [isCompactMode, isComposerExpanded])

    useEffect(() => {
        const openComposerFromNav = () => {
            setIsComposerExpanded(true)
            window.setTimeout(() => focusComposerEditor(), 0)
        }

        window.addEventListener("edusiia:open-compose", openComposerFromNav)

        return () => {
            window.removeEventListener("edusiia:open-compose", openComposerFromNav)
        }
    }, [])

    const {
        isUnsavedChangesModalOpen,
        confirmNavigation,
        cancelNavigation,
    } = useUnsavedChangesWarning(hasUnsavedPostChanges)

    const removeFile = () => {
        setPreviewUrl("")
        setImage(null)
        setDocumentState(null)
        if (fileRef.current) {
            fileRef.current.value = ""
        }
    }

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files

        if (files) {
            const reader = new FileReader()
            reader.readAsDataURL(files[0])

            reader.addEventListener("load", () => {
                if (fileImageType.includes(files[0].type)) {
                    setImage(files[0])
                    setDocumentState(null)
                } else {
                    setDocumentState(files[0])
                    setImage(null)
                }
                setFileType(files[0].type)
                setPreviewUrl(URL.createObjectURL(files[0]))
            })
        }
    }

    const createPost = async () => {
        if (isSubmitting) {
            return
        }
        setIsSubmitting(true)
        try {
            const objectId = role === RoleType.USER ? userDataState.id : organizationDataState?.id
            const mentionedUsersArray: MentionedUsersType[] = []
            let modifiedContent = ""
            if (editorRef.current!) {
                const htmlContent = ConvertStringToHTML(editorRef!.current!.getContent({ format: 'raw' }))
                const tagSpans = htmlContent.querySelectorAll("[data-type]")

                tagSpans.forEach((span, index) => {
                    span.innerHTML = "@tagged"
                    mentionedUsersArray.splice(index, 0, {
                        tagged_user: span.getAttribute("data-type") === RoleType.USER.toLocaleLowerCase() ? parseInt(span.id) : null,
                        tagged_org: span.getAttribute("data-type") === RoleType.ORGANIZATION.toLocaleLowerCase() ? parseInt(span.id) : null,
                    })
                    modifiedContent = htmlContent.outerHTML.replaceAll("<body>", "").replaceAll("</body>", "").replace(hashtagRegex, "$1")
                })

                if (mentionedUsersArray.length >= 1 || editorRef.current!.getContent({ format: "text" }).length > 0) {
                    const formData = new FormData()

                    formData.append("content", modifiedContent.length > 0 ? modifiedContent : editorRef!.current!.getContent({ format: 'raw' }).replace(hashtagRegex, "$1"))
                    formData.append("content_type", role.toLowerCase())
                    formData.append("object_id", objectId?.toString()!)
                    formData.append("tags", JSON.stringify(mentionedUsersArray))
                    formData.append("source", "FEED")

                    formData.append("room", roomId.toString())
                    image !== null && formData.append("post_file", image)
                    documentState !== null && formData.append("post_file", documentState)
                    fileType.length > 0 && formData.append("file_type", fileType)
                    const wasPostCreated = await dispatch<any>(roomPostPostRequest(formData, posts))

                    if (wasPostCreated) {
                        editorRef.current!.setContent('')
                        setImage(null)
                        setDocumentState(null)
                        setPreviewUrl(undefined)
                        setFileType("")
                        if (fileRef.current) {
                            fileRef.current.value = ""
                        }
                        isCompactMode && setIsComposerExpanded(false)
                        setDropdownKey(Math.floor(Math.random() * 100))
                        setIsDraftNoticeVisible(false)
                        SessionService.removeItem(draftStorageKey)

                        window.dispatchEvent(new CustomEvent("edusiia:feed-post-created"))

                        const element = document.getElementsByClassName("feed-item-profile-card")[0]
                        element?.scrollIntoView({ behavior: "smooth" })
                    }
                }
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <UnsavedChangesModal
                isOpen={isUnsavedChangesModalOpen}
                title={translation.unsavedChangesTitle}
                message={translation.unsavedChangesMessage}
                cancelLabel={translation.cancel}
                confirmLabel={translation.leavePage}
                onCancel={cancelNavigation}
                onConfirm={confirmNavigation}
            />

            {isCompactMode && isComposerExpanded && <div className="feed-item__composer-backdrop" onClick={() => setIsComposerExpanded(false)} />}

            {!shouldHideCollapsedComposer && (
                <div
                    className={`feed-item ${isCompactMode && !isComposerExpanded ? "feed-item--composer-compact-overlay" : ""} ${isCompactMode && isComposerExpanded ? "feed-item--composer-focus" : ""}`}
                    data-tutorial-anchor="feed-textbox"
                    data-feed-compose-root="true"
                    key="feed-compose-text-box"
                >
                    <TutorialBubble text={translation.tutorial_user_feed_textbox} targetSelector='[data-tutorial-anchor="feed-textbox"]' placement="right" visible={userDataState.tutorial_progress === 3} hasNext={true} />
                    {isDraftNoticeVisible && (
                        <div className="feed-item__draft-restored-banner">
                            <span>{translation.draftRestoredMessage}</span>
                            <span className="feed-item__draft-restored-discard" onClick={discardDraft}>{translation.discardDraft}</span>
                        </div>
                    )}
                    {isCompactMode && !isComposerExpanded ? (
                        <div className="feed-item__composer-compact" onClick={onCompactComposerClick}>
                            <div className="feed-item__composer-compact-placeholder">
                                <div className="feed-item__composer-compact-icon">
                                    <EditProfileSmallIcon />
                                </div>
                                <div className="feed-item__composer-compact-text">{translation.typeYourMessage}</div>
                            </div>
                        </div>
                    ) : (
                        <div className={`feed-item__card ${isCompactMode && isComposerExpanded ? "feed-item__card--composer-focus" : ""}`}>
                        <div className="feed-item__editor-container">
                        {isCompactMode && (
                            <div className="feed-item__composer-close-row">
                                <div className="feed-item__composer-close" onClick={closeComposer}>
                                    <CloseIcon />
                                </div>
                            </div>
                        )}
                        <div className="feed-item__editor">
                            <EditorTextArea
                                inputState={inputState}
                                setInputState={setInputState}
                                editorRef={editorRef}
                                editorPlaceholder={translation.typeYourMessage}
                                setContent={setContent}
                                editorHeight={160}
                                modifier={EditorModifier.feedTextArea}
                                initialEditorValue=""
                                enterPressed={() => createPost()}
                                isUserDashboard={true}
                            />
                                <CharacterCounter current={content.length} max={contentMaxLength} modifier="margin-left" showWarning />
                        </div>
                        <div className="feed-item__character-counter-container">
                            {content.length > contentMaxLength && ` Der Inhalt überschreitet die zulässige Anzahl von ${contentMaxLength} Zeichen.`}
                        </div>

                        {photoSizeErrorState && <p className="room-layout__error-message">{translation.fileIsTooBig}</p>}
                        {postContentErrorState.length > 0 && <p className="room-layout__error-message">{postContentErrorState}</p>}

                        <div className="feed-item__editor-action-container">
                            <div className="feed-item__editor-share-text"><div className="room-dropdown__cube-icon">
                                <CubeIcon />
                            </div></div>
                            <div className="feed-item__room-dropdown">
                                <SearchableRoomDropdown hasTooltip={false} itemId={roomId} setItemId={setRoomId} validationError={validationError} key={dropdownKey} />
                            </div>
                            {/* <div
                                ref={emojiButtonRef}
                                onClick={() => setIsEmojiSelectorOpen(!isEmojiSelectorOpen)}
                                className="room-layout__right-panel-icon"
                            >
                                <SmileIcon />
                            </div>
                            <EmojiPickerPortal
                                isOpen={isEmojiSelectorOpen}
                                onEmojiClick={onEmojiClick}
                                onClose={() => setIsEmojiSelectorOpen(false)}
                                triggerElementRef={emojiButtonRef as any}
                            /> */}

                            <div className="room-layout__right-panel-icon">
                                <input className="room-layout__input-file" type="file" accept={acceptedFileTypes} onChange={onFileChange} ref={fileRef} />
                                <FileIcon />
                            </div>

                            {isSubmitting ? (
                                <div className="room-layout__right-panel-icon">
                                    <div className="feed-item__send-icon--sending" />
                                </div>
                            ) : content.length > 0 && roomId > 0 && content.length <= contentMaxLength ? (
                                <div className="room-layout__right-panel-icon" onClick={createPost}>
                                    <SendIcon color="green" />
                                </div>
                            ) : (
                                <div className="room-layout__right-panel-icon room-layout__right-panel-icon--disable">
                                    <SendIcon color="grey" />
                                </div>
                            )}
                        </div>

                        {content.length > 0 && roomId === 0 && !isSubmitting && (
                            <p className="feed-item__hint-message">{translation.pleaseSelectARoomToPost}</p>
                        )}

                        {image && (
                            <div className="room-layout__post-image-container">
                                <Image className="room-layout__post-image" src={previewUrl} alt="post" />
                                <div className="room-layout__post-image-cancel-container" onClick={removeFile}>
                                    <Image src={previewUrl} alt="post" className="room-layout__post-image room-layout__post-image--opacity" />
                                    <div className="room-layout__post-image-cancel">
                                        <CloseIcon />
                                    </div>
                                </div>
                            </div>
                        )}
                        {documentState && (
                            <DocumentTextAreaPreview document_name={documentState.name} document_size={documentState.size} onRemoveFile={removeFile} />
                        )}
                        </div>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default FeedTextBox
